/**
 * "My lists" settings of the mailbox owner (docs/plan/lists-feeds.md 7): the user-scoped lists an administrator defined
 * (allowed or blocked senders and domains, blocked attachment names, ...) and the password key lists for decrypting
 * attachments. One component for webmail (self) behind an adapter. The administrator decides which lists exist, whether
 * users may add entries and how many; the effect of a list follows its kind and never overrides organisation policy.
 */
import { useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useI18n, type Translate } from '@platform/i18n';
import type { ScopedListChange, ScopedListEntries, ScopedListView, ScopedLists, ScopedPasswordView, TypedList } from '@platform/api-client';
import { KeyRoundIcon, ListChecksIcon, PlusIcon, UploadIcon, XIcon } from 'lucide-react';
import { Button } from '../components/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/dialog';
import { Input, Textarea } from '../components/input';
import { Label } from '../components/label';
import { MenuSelect } from '../components/menu-select';
import { Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/tabs';
import { EmptyState, ErrorState } from '../composites/states';
import { PageBody, PageHeader } from '../composites/page';
import { useAsync } from '../hooks/use-async';
import { errorCode, useErrorText } from '../connected/errors';
import { PasswordSharesPanel, type SharesAdapter } from '../typed-lists/shares-panel';

export interface ScopedListsAdapter extends SharesAdapter {
  overview(): Promise<ScopedLists>;
  entries(listId: string): Promise<ScopedListEntries>;
  add(listId: string, values: string[]): Promise<ScopedListChange>;
  remove(listId: string, values: string[]): Promise<ScopedListChange>;
  importCsv(listId: string, csv: string): Promise<ScopedListChange>;
  /** The full record of a password list (needed by the sharing tab). */
  passwordList(id: string): Promise<TypedList>;
  putKey(id: string, keyId: string, input: { expectedRevision: number; label: string; value: string }): Promise<TypedList>;
  deleteKey(id: string, keyId: string, input: { expectedRevision: number }): Promise<TypedList>;
  shareGroups: NonNullable<SharesAdapter['shareGroups']>;
}

/** Largest CSV the API accepts. */
const MAX_CSV_BYTES = 512 * 1024;
const FILTER_ROWS = 15;
const splitValues = (text: string): string[] => text.split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean);
/** Error codes of the entry normaliser that have their own wording; anything else gets the generic text. */
const ENTRY_ERRORS = ['empty', 'too_long', 'invalid_address', 'invalid_domain', 'wildcard_not_allowed', 'invalid_filetype'] as const;
/** Key id of a password label: lower-case letters, digits and dashes (the API's id format). */
const keyIdOf = (label: string): string => label.toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 56) || 'key';

const LIST_PREFIX = 'list:';
const PASSWORD_PREFIX = 'password:';

/** Rejections of the add and import calls as one toast reason. */
function rejectedReason(change: ScopedListChange, t: Translate): string {
  const codes = [...new Set(change.rejected.map((row) => row.code))];
  return codes.map((code) => t((ENTRY_ERRORS as readonly string[]).includes(code) ? `scopedLists.error.${code as (typeof ENTRY_ERRORS)[number]}` : 'scopedLists.error.invalid')).join(', ');
}

function ImportDialog({ list, busy, onClose, onImport }: { list: ScopedListView; busy: boolean; onClose: () => void; onImport: (csv: string) => Promise<boolean> }) {
  const { t } = useI18n();
  const [csv, setCsv] = useState('');
  const [problem, setProblem] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);
  const textId = useId();
  const tooLarge = new Blob([csv]).size > MAX_CSV_BYTES;
  const submit = async () => {
    if (csv.trim() === '' || tooLarge) return;
    if (await onImport(csv)) onClose();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('scopedLists.import.title', { list: list.name })}</DialogTitle>
          <DialogDescription>{t('scopedLists.import.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={() => fileInput.current?.click()}><UploadIcon />{t('scopedLists.import.chooseFile')}</Button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              className="sr-only"
              tabIndex={-1}
              aria-label={t('scopedLists.import.file')}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                setProblem(undefined);
                void file.text().then(setCsv, () => setProblem(t('scopedLists.fileUnreadable')));
              }}
            />
            <span className="text-xs text-muted-foreground">{t('scopedLists.import.limit')}</span>
          </div>
          <div className="space-y-1">
            <Label htmlFor={textId}>{t('scopedLists.import.text')}</Label>
            <Textarea id={textId} value={csv} rows={8} onChange={(event) => setCsv(event.target.value)} placeholder={t('scopedLists.import.placeholder')} disabled={busy} />
          </div>
          {(problem || tooLarge) && <p className="text-sm text-destructive" role="alert">{problem ?? t('scopedLists.import.tooLarge')}</p>}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="button" disabled={busy || csv.trim() === '' || tooLarge} onClick={() => void submit()}>{t('scopedLists.import.submit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EntriesPanel({ list, values, busy, onAdd, onRemove, onImport }: {
  list: ScopedListView;
  values: readonly string[];
  busy: boolean;
  onAdd: (values: string[]) => Promise<boolean>;
  onRemove: (value: string) => void;
  onImport: (csv: string) => Promise<boolean>;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const inputId = useId();
  const type = list.type === 'attachment_name' ? 'attachmentName' : list.type;
  const needle = filter.trim().toLowerCase();
  const shown = needle ? values.filter((value) => value.toLowerCase().includes(needle)) : values;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = splitValues(draft);
    if (parsed.length === 0 || busy) return;
    if (await onAdd(parsed)) setDraft('');
  };
  return (
    <div className="min-w-0 space-y-3" data-testid="scoped-list-panel">
      {list.usersMayAdd ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void submit(event)}>
          <div className="min-w-48 flex-1 space-y-1">
            <Label htmlFor={inputId}>{t('scopedLists.addLabel')}</Label>
            <Input id={inputId} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={t(`scopedLists.placeholder.${type}`)} autoComplete="off" disabled={busy} />
          </div>
          <Button type="submit" disabled={busy || splitValues(draft).length === 0}><PlusIcon />{t('scopedLists.add')}</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={() => setImporting(true)}><UploadIcon />{t('scopedLists.import.open')}</Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground" role="note">{t('scopedLists.removeOnly')}</p>
      )}
      {list.usersMayAdd && <p className="text-xs text-muted-foreground">{t(`scopedLists.hint.${type}`)}</p>}
      {values.length > FILTER_ROWS && (
        <Input aria-label={t('scopedLists.filter')} className="max-w-xs" placeholder={t('scopedLists.filter')} value={filter} onChange={(event) => setFilter(event.target.value)} />
      )}
      {values.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('scopedLists.empty')}</p>
      ) : (
        <div className="max-h-72 overflow-y-auto rounded-md border">
          <table className="w-full text-sm" aria-label={t('scopedLists.table', { list: list.name })}>
            <tbody>
              {shown.map((value) => (
                <tr key={value} className="border-b last:border-0">
                  <td className="min-w-0 px-3 py-1 break-all">{value}</td>
                  <td className="w-12 px-1 py-1 text-right">
                    <Button variant="ghost" size="sm" disabled={busy} aria-label={t('scopedLists.remove', { value })} onClick={() => onRemove(value)}><XIcon /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {needle && shown.length === 0 && values.length > 0 && <p className="text-sm text-muted-foreground">{t('scopedLists.noMatch')}</p>}
      {importing && <ImportDialog list={list} busy={busy} onClose={() => setImporting(false)} onImport={onImport} />}
    </div>
  );
}

function PasswordPanel({ password, adapter, busy, onSave, onRemove, onShared, onFailure }: {
  password: ScopedPasswordView;
  adapter: ScopedListsAdapter;
  busy: boolean;
  onSave: (keyId: string, label: string, value: string) => Promise<boolean>;
  onRemove: (keyId: string) => void;
  onShared: (saved: TypedList) => void;
  onFailure: (message: string) => void;
}) {
  const { t, formatDate } = useI18n();
  const labelId = useId();
  const valueId = useId();
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const detail = useAsync(() => adapter.passwordList(password.id), `${password.id}:${password.revision}`);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || label.trim() === '' || secret === '') return;
    if (await onSave(keyIdOf(label), label.trim(), secret)) { setLabel(''); setSecret(''); }
  };
  return (
    <Tabs defaultValue="keys" key={password.id}>
      <TabsList>
        <TabsTrigger value="keys">{t('scopedLists.password.keysTab')}</TabsTrigger>
        <TabsTrigger value="sharing">{t('scopedLists.password.sharingTab')}</TabsTrigger>
      </TabsList>
      <TabsContent value="keys" className="min-w-0 space-y-3">
        <p className="text-sm text-muted-foreground">{t('scopedLists.password.hint')}</p>
        {password.keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('scopedLists.password.empty')}</p>
        ) : (
          <ul className="space-y-1" aria-label={t('scopedLists.password.keys')}>
            {password.keys.map((key) => (
              <li key={key.id} className="flex items-center justify-between gap-2 rounded border px-3 py-1 text-sm">
                <span className="min-w-0 break-all">{key.label} <span className="text-xs text-muted-foreground">{formatDate(key.updatedAt, { dateStyle: 'medium' })}</span></span>
                <Button variant="ghost" size="sm" disabled={busy} aria-label={t('scopedLists.password.remove', { name: key.label })} onClick={() => onRemove(key.id)}><XIcon /></Button>
              </li>
            ))}
          </ul>
        )}
        <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void submit(event)}>
          <div className="min-w-40 flex-1 space-y-1">
            <Label htmlFor={labelId}>{t('scopedLists.password.label')}</Label>
            <Input id={labelId} value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} autoComplete="off" disabled={busy} />
          </div>
          <div className="min-w-40 flex-1 space-y-1">
            <Label htmlFor={valueId}>{t('scopedLists.password.value')}</Label>
            <Input id={valueId} type="password" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="new-password" disabled={busy} />
          </div>
          <Button type="submit" disabled={busy || label.trim() === '' || secret === ''}><KeyRoundIcon />{t('scopedLists.password.save')}</Button>
        </form>
      </TabsContent>
      <TabsContent value="sharing">
        {password.revision === 0 ? (
          <p className="text-sm text-muted-foreground">{t('scopedLists.password.shareFirst')}</p>
        ) : detail.error ? (
          <p className="text-sm text-destructive" role="alert">{t('typedLists.sharing.loadError')}</p>
        ) : !detail.data ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <PasswordSharesPanel key={`${detail.data.id}:${String(detail.data.revision)}`} list={detail.data} adapter={adapter} disabled={busy} onSaved={onShared} onFailure={onFailure} />
        )}
      </TabsContent>
    </Tabs>
  );
}

export function ScopedListsManager({ adapter, title, description, headerTabs }: {
  adapter: ScopedListsAdapter;
  title?: string;
  description?: string;
  headerTabs?: ReactNode;
}) {
  const { t, formatNumber } = useI18n();
  const errors = useErrorText();
  const state = useAsync(() => adapter.overview(), 'scoped-lists');
  const [overview, setOverview] = useState<ScopedLists>();
  const [selection, setSelection] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const current = overview ?? state.data;

  const choices = useMemo(() => [
    ...(current?.lists ?? []).map((list) => ({ value: `${LIST_PREFIX}${list.id}`, label: list.name })),
    ...(current?.passwords ?? []).map((password) => ({ value: `${PASSWORD_PREFIX}${password.id}`, label: t('scopedLists.password.option', { name: password.name }) })),
  ], [current, t]);
  const selected = choices.find((choice) => choice.value === selection)?.value ?? choices[0]?.value;
  const list = selected?.startsWith(LIST_PREFIX) ? current?.lists.find((item) => `${LIST_PREFIX}${item.id}` === selected) : undefined;
  const password = selected?.startsWith(PASSWORD_PREFIX) ? current?.passwords.find((item) => `${PASSWORD_PREFIX}${item.id}` === selected) : undefined;

  // Entries of the chosen list; changes replace them with the server's answer.
  const [entryView, setEntryView] = useState<{ id: string; values: string[] }>();
  const entries = useAsync(() => (list ? adapter.entries(list.id) : Promise.resolve(undefined)), list?.id ?? 'none');
  const values = entryView?.id === list?.id ? entryView?.values : entries.data?.values;

  const describe = (error: unknown): string => {
    switch (errorCode(error)) {
      case 'scoped_list_full': return t('scopedLists.full', { limit: formatNumber(list?.limit ?? 0) });
      case 'scoped_lists_per_user_limit': return t('scopedLists.perUserLimit');
      case 'scoped_list_batch_too_large': return t('scopedLists.batchTooLarge');
      case 'user_entries_disabled': return t('scopedLists.entriesDisabled');
      case 'scoped_list_not_found': return t('scopedLists.notFound');
      default: return errors.describe(error);
    }
  };
  const run = async <T,>(action: () => Promise<T>, done: (result: T) => void): Promise<boolean> => {
    setBusy(true);
    setFailure(undefined);
    try {
      done(await action());
      return true;
    } catch (error) {
      setFailure(describe(error));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const patch = (update: (view: ScopedLists) => ScopedLists) => setOverview((now) => {
    const base = now ?? state.data;
    return base && update(base);
  });
  const reportChange = (change: ScopedListChange) => {
    setEntryView({ id: change.list.id, values: change.values });
    patch((view) => ({ ...view, lists: view.lists.map((item) => (item.id === change.list.id ? change.list : item)) }));
    if (change.added > 0) toast.success(t('scopedLists.added', { n: change.added }));
    if (change.removed > 0) toast.success(t('scopedLists.removed', { n: change.removed }));
    if (change.rejected.length > 0) toast.error(t('scopedLists.rejected', { n: change.rejected.length, reason: rejectedReason(change, t) }));
  };
  const change = (action: (id: string) => Promise<ScopedListChange>): Promise<boolean> => (list ? run(() => action(list.id), reportChange) : Promise.resolve(false));
  const applyPassword = (saved: TypedList) => {
    const keys = (saved.keys ?? []).map((key) => ({ id: key.id, label: key.label, revision: key.revision, updatedAt: key.updatedAt }));
    patch((view) => ({ ...view, passwords: view.passwords.map((item) => (item.id === saved.id ? { ...item, revision: saved.revision, keys, shares: saved.shares?.length ?? item.shares } : item)) }));
  };

  const header = <PageHeader title={title ?? t('scopedLists.title')} description={description ?? t('scopedLists.description')} tabs={headerTabs} />;
  if (state.error && !current) {
    return <>{header}<PageBody><ErrorState description={errors.describe(state.error)} onRetry={state.reload} /></PageBody></>;
  }
  if (current && choices.length === 0) {
    return <>{header}<PageBody><EmptyState icon={ListChecksIcon} title={t('scopedLists.none')} description={t('scopedLists.noneHint')} /></PageBody></>;
  }
  return (
    <>
      {header}
      <PageBody>
        <p className="mb-4 text-sm text-muted-foreground" role="note">{t('scopedLists.policyNote')}</p>
        {failure && <p className="mb-3 text-sm text-destructive" role="alert">{failure}</p>}
        {!current || !selected ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <section className="min-w-0 space-y-3 rounded-md border p-4" aria-label={t('scopedLists.section')}>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="min-w-0 space-y-1">
                <Label>{t('scopedLists.select')}</Label>
                <MenuSelect<string> label={t('scopedLists.select')} value={selected} options={choices} onChange={(next) => { setFailure(undefined); setSelection(next); }} className="w-full min-w-56 sm:w-80" />
              </div>
              {list && <span className="text-xs text-muted-foreground">{t('scopedLists.count', { n: formatNumber(list.count), limit: formatNumber(list.limit) })}</span>}
            </div>
            {list?.description && <p className="text-sm">{list.description}</p>}
            {list && (entries.loading && values === undefined ? (
              <Skeleton className="h-24 w-full" />
            ) : entries.error && values === undefined ? (
              <ErrorState description={errors.describe(entries.error)} onRetry={entries.reload} />
            ) : (
              <EntriesPanel
                key={list.id}
                list={list}
                values={values ?? []}
                busy={busy}
                onAdd={(added) => change((id) => adapter.add(id, added))}
                onRemove={(value) => void change((id) => adapter.remove(id, [value]))}
                onImport={(csv) => change((id) => adapter.importCsv(id, csv))}
              />
            ))}
            {password && (
              <PasswordPanel
                password={password}
                adapter={adapter}
                busy={busy}
                onSave={(keyId, label, value) => run(() => adapter.putKey(password.id, keyId, { expectedRevision: password.revision, label, value }), (saved) => { applyPassword(saved); toast.success(t('scopedLists.password.saved')); })}
                onRemove={(keyId) => void run(() => adapter.deleteKey(password.id, keyId, { expectedRevision: password.revision }), applyPassword)}
                onShared={applyPassword}
                onFailure={setFailure}
              />
            )}
          </section>
        )}
      </PageBody>
    </>
  );
}
